// frontend/src/pages/SellCarPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehiclesApi } from '../api/vehicles';
import { storesApi } from '../api/stores'; 
import { walletApi } from '../api/wallet'; 
import uploadService from '../services/upload';
import { compressImage } from '../utils/imageCompression';
import { useAuth } from '../context/AuthContext';
import { settingsApi } from '../api/settings';
import { aiApi } from '../api/ai';
import CameraModal from '../components/common/CameraModal';
import CreditPurchaseModal from '../components/common/CreditPurchaseModal';
import BankTransferInfo from '../components/common/BankTransferInfo';
import helpers from '../utils/helpers';
import { 
    FaCheckCircle, FaCar, FaSpinner, FaInfoCircle, 
    FaCamera, FaFolderOpen, FaTrash, FaStore, FaGem, FaMoneyBillWave, FaExclamationTriangle, FaUserShield, FaClipboardCheck, FaWallet, FaMagic, FaSyncAlt, FaTimes 
} from 'react-icons/fa';

function SellCarPage() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // --- SETTINGS STATE ---
  const [settings, setSettings] = useState(null); 
  const [premiumConfig, setPremiumConfig] = useState(null); 
  const [pricingConfig, setPricingConfig] = useState(null); 
  
  const [myStores, setMyStores] = useState([]); 
  const [realTimeBalance, setRealTimeBalance] = useState(0);
  
  // --- LOCATION DATA ---
  const [lgas, setLgas] = useState([]); 
  
  const [currentListingCount, setCurrentListingCount] = useState(0);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [activeViewType, setActiveViewType] = useState(null);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);

  // Form Data State
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    condition: 'nigerian_used',
    transmission: 'automatic',
    mileage: '',
    vin: '',
    color: '',
    location_state: '', 
    location_lga: '',
    description: '',
    features: '',
    store_id: '', 
    pay_on_delivery_status: 'Available',
    refundable_status: 'Not Specified',
    request_inspection: false 
  });

  // --- AI DESCRIPTION HELPER ---
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOptions, setAiOptions] = useState([]);
  const [aiError, setAiError] = useState('');

  // Shown when the customer tries to pay but the wallet is short.
  const [showFundModal, setShowFundModal] = useState(false);
  const [fundShortfall, setFundShortfall] = useState({ total: 0, balance: 0 });

  const generateDescriptions = async () => {
    setAiError('');
    setAiLoading(true);
    setAiOpen(true);
    try {
      const res = await aiApi.generateVehicleDescriptions({
        make: formData.make,
        model: formData.model,
        year: formData.year ? parseInt(formData.year) : null,
        condition: (formData.condition || '').replace(/_/g, ' '),
        transmission: formData.transmission,
        mileage: formData.mileage ? String(formData.mileage) : null,
        color: formData.color,
        price: formData.price ? String(formData.price) : null,
        features: formData.features,
        count: 3,
      });
      const opts = (res && res.descriptions) || [];
      if (opts.length === 0) {
        setAiError(res?.message || 'No descriptions came back. Please try again or write your own.');
      }
      setAiOptions(opts);
    } catch (err) {
      setAiError(err?.message || 'Could not reach the AI. Please try again or write your own.');
      setAiOptions([]);
    } finally {
      setAiLoading(false);
    }
  };

  const chooseDescription = (text) => {
    setFormData(prev => ({ ...prev, description: text }));
    setAiOpen(false);
  };

  const [imageUrls, setImageUrls] = useState({
    front: null,
    back: null,
    interior: null,
    engine: null,
    side: null,
    boot: null,
    dashboard: null,
    tire: null,
    vin_label: null,
    front_seats: null,
    center_console: null
  });

  const [uploadingSlot, setUploadingSlot] = useState(null);

  // --- LOCAL DRAFT (browser autosave) ---
  // Keeps the customer's typing + uploaded photo URLs so an empty wallet
  // doesn't wipe their work. Per user, in THIS browser. A proper server
  // draft (visible in the profile, on any device) comes next.
  const DRAFT_KEY = `ea_sellcar_draft_${user?.id || 'guest'}`;
  const [draftRestored, setDraftRestored] = useState(false);

  const saveLocalDraft = (fd, imgs) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ formData: fd, imageUrls: imgs, savedAt: Date.now() }));
    } catch (e) { /* storage may be full/blocked; ignore */ }
  };

  const clearLocalDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
  };

  // Restore a saved draft (once), so returning to this page resumes the form.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.formData) setFormData(prev => ({ ...prev, ...saved.formData }));
        if (saved && saved.imageUrls) setImageUrls(prev => ({ ...prev, ...saved.imageUrls }));
        // Only show the "restored" note if there was real content.
        const hasContent = saved?.formData && (saved.formData.make || saved.formData.model || saved.formData.description);
        if (hasContent) setDraftRestored(true);
      }
    } catch (e) { /* ignore corrupt draft */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save whenever the form or photos change (after first restore pass).
  useEffect(() => {
    const t = setTimeout(() => saveLocalDraft(formData, imageUrls), 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, imageUrls]);

  useEffect(() => {
      const init = async () => {
          try {
              if (reloadUser) await reloadUser();

              const [s, storesData, count, lgasData] = await Promise.all([
                  settingsApi.getAllSettings(),
                  storesApi.getMyStores(),
                  vehiclesApi.getUserListingCount(),
                  import('../api/zones').then(m => m.zonesApi.getAllLgas())
              ]);
              
              setSettings(s.media_config || {}); 
              setPremiumConfig(s.premium_config || {}); 
              setPricingConfig(s.pricing || {}); 
              
              setMyStores(storesData || []);
              setCurrentListingCount(count || 0);
              setLgas(lgasData || []); 
              
              try {
                  const freshUser = await import('../services/auth').then(m => m.default.fetchCurrentUserFromServer());
                  if (freshUser.success) {
                      const bal = freshUser.data.wallet_assets?.credit_balance || 0;
                      setRealTimeBalance(bal);
                  }
              } catch (wErr) {
                  console.error("Wallet fetch error", wErr);
              }

          } catch (e) { console.error(e); }
      };
      init();
  }, []);

  // --- DYNAMIC LOCATION LOGIC ---
  const groupedLgas = React.useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga.lga);
      return acc;
    }, {});
  }, [lgas]);

  const availableLgas = formData.location_state ? groupedLgas[formData.location_state] || [] : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'location_state') {
        setFormData({ 
            ...formData, 
            location_state: value, 
            location_lga: '' 
        });
    } else {
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const isAdminBypass = user?.roles?.some(r => ['admin', 'super_admin'].includes(r));

  // --- COST CALCULATOR ---
  const calculateTotalCost = () => {
      let total = 0;
      let breakdown = [];
      
      // 1. Spam Stopper
      const maxFree = premiumConfig?.max_free_listings ?? 20; 
      const listingPrice = premiumConfig?.price_per_listing || 1000;
      
      if (!isAdminBypass && currentListingCount >= maxFree) {
          total += listingPrice;
          // --- UPDATED LABEL FOR CLARITY ---
          breakdown.push({ 
              label: `Listing Fee (Free limit of ${maxFree} listings exceeded)`, 
              amount: listingPrice 
          });
      }

      // 2. Image Limit
      const imageCount = Object.values(imageUrls).filter(v => v).length;
      const freeImages = premiumConfig?.free_image_limit || 4;
      const hasCredits = (user?.wallet_assets?.listing_credits || 0) > 0;

      if (!isAdminBypass && imageCount > freeImages) {
          if (hasCredits) {
              breakdown.push({ label: `Premium Images (${imageCount})`, amount: 0, note: "1 Credit will be used" });
          } else {
              const imageCost = premiumConfig?.listing_credit_price_1 || 1500;
              total += imageCost;
              breakdown.push({ label: `Premium Images (${imageCount})`, amount: imageCost, note: "Pay from Wallet" });
          }
      }

      // 3. Inspection Fee
      if (formData.request_inspection) {
          const inspectionFee = pricingConfig?.inspection_fee || 15000;
          total += inspectionFee;
          breakdown.push({ label: "Vehicle Inspection Request", amount: inspectionFee });
      }

      return { total, breakdown };
  };

  const checkLimit = () => {
      if (isAdminBypass) return true;

      const currentImageCount = Object.values(imageUrls).filter(v => v).length;
      const freeLimit = premiumConfig?.free_image_limit || 4;
      
      if (currentImageCount < freeLimit) return true;

      const hasCredits = (user?.wallet_assets?.listing_credits || 0) > 0;
      if (hasCredits) return true;

      const imageCost = premiumConfig?.listing_credit_price_1 || 1500;
      const { total: currentTotal } = calculateTotalCost();
      const projectedTotal = currentTotal + imageCost;

      if (realTimeBalance >= projectedTotal) {
          const confirmUse = window.confirm(
              `⚠️  PAYMENT REQUIRED  ⚠️\n\n` +
              `This extra image costs:\n` +
              `>>>  ${helpers.formatCurrency(imageCost, 'NGN')}  <<<\n\n` +
              `Your Wallet Balance: ${helpers.formatCurrency(realTimeBalance, 'NGN')}.\n\n` +
              `Click OK to use your Wallet Balance (deducted at submission).\n` +
              `Click Cancel to buy Listing Credits instead.`
          );
          if (confirmUse) return true;
      } else {
          alert(
              `❌ INSUFFICIENT FUNDS ❌\n\n` +
              `Cost for this image: ${helpers.formatCurrency(imageCost, 'NGN')}\n` +
              `Your Balance: ${helpers.formatCurrency(realTimeBalance, 'NGN')}\n\n` +
              `Please purchase Listing Credits to continue.`
          );
      }

      setIsCreditModalOpen(true); 
      return false; 
  };

  const handleCameraClick = (viewType) => {
      if (!checkLimit()) return; 
      setActiveViewType(viewType);
      setIsCameraOpen(true);
  };

  const handleCameraCapture = async (file) => {
      await processFile(file, activeViewType);
      setIsCameraOpen(false);
  };

  const processFile = async (file, viewType) => {
      if (!file) return;
      setUploadingSlot(viewType);
      setError(null);
      try {
          const compressionQuality = settings?.vehicle_compression || 0.4;
          const compressed = await compressImage(file, compressionQuality);
          const folderPath = `vehicle-sale-images/${user.id}/${Date.now()}`;
          const response = await uploadService.uploadImage(compressed, folderPath);
          setImageUrls(prev => ({ ...prev, [viewType]: response.url }));
      } catch (err) {
          setError(`Failed to upload ${viewType}.`);
      } finally {
          setUploadingSlot(null);
      }
  };

  const handleFileInput = (e, viewType) => {
      if (!checkLimit()) { e.target.value = ""; return; }
      processFile(e.target.files[0], viewType);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!imageUrls.front) {
      setError("The Front View image is required.");
      setLoading(false);
      return;
    }

    const { total } = calculateTotalCost();
    
    if (total > 0 && realTimeBalance < total) {
        // Pop up the payment options right where the customer is, so they
        // don't have to scroll anywhere to find how to pay.
        setFundShortfall({ total, balance: realTimeBalance });
        setShowFundModal(true);
        setError(`Insufficient Funds. Total Cost: ${helpers.formatCurrency(total, 'NGN')}. Your Balance: ${helpers.formatCurrency(realTimeBalance, 'NGN')}. Please top up your wallet.`);
        setLoading(false);
        return;
    }

    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        mileage: parseInt(formData.mileage),
        features: formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f !== '') : [],
        images: Object.fromEntries(Object.entries(imageUrls).filter(([_, v]) => v != null))
      };

      if (formData.vin) {
          payload.description = `VIN: ${formData.vin}\n\n${payload.description}`;
      }

      await vehiclesApi.createListing(payload);
      clearLocalDraft();
      setSuccess(true);
      window.scrollTo(0, 0);

    } catch (err) {
      setError(err.message || "Failed to submit listing.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Listing Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Your vehicle has been submitted.
            {formData.request_inspection && " An inspection request has been generated."}
          </p>
          <button onClick={() => navigate('/account')} className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded transition duration-300">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { total: totalCost, breakdown } = calculateTotalCost();
  const dynamicInspectionFee = pricingConfig?.inspection_fee || 15000;

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <FaCar className="text-primary" /> Sell Your Car
            </h1>
            
            <div className="flex items-center gap-3">
                <div className="bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200 flex items-center gap-2 text-sm">
                    <FaWallet className="text-green-600" />
                    <span className="text-gray-600 hidden sm:inline">Balance:</span>
                    <span className="font-bold text-gray-800">{helpers.formatCurrency(realTimeBalance, 'NGN')}</span>
                </div>

            </div>
        </div>

        {isAdminBypass && (
            <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-800 p-4 mb-6 rounded-r shadow-sm flex items-start gap-3 animate-fadeIn">
                <FaUserShield className="text-xl mt-1" />
                <div>
                    <h3 className="font-bold">Admin Mode Active</h3>
                    <p className="text-sm">Listing Limits and Fees are bypassed.</p>
                </div>
            </div>
        )}


        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 md:p-8">
          
          {myStores.length > 0 && (
              <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-6">
                  <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center gap-2">
                      <FaStore /> Select Dealership Location (Optional)
                  </label>
                  <select name="store_id" value={formData.store_id} onChange={handleChange} className="p-2 border rounded w-full bg-white focus:ring-2 focus:ring-blue-500">
                      <option value="">-- Selling as Private Individual --</option>
                      {myStores.map(store => (
                          <option key={store.id} value={store.id}>{store.store_name} ({store.lga_name})</option>
                      ))}
                  </select>
              </div>
          )}

          {draftRestored && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start justify-between gap-3">
              <p className="text-sm text-green-800">
                <span className="font-bold">We saved your progress.</span> You can finish and submit once your wallet is funded.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Clear the saved form and start over?')) {
                    clearLocalDraft();
                    window.location.reload();
                  }
                }}
                className="text-xs font-semibold text-green-700 underline flex-shrink-0"
              >
                Clear form
              </button>
            </div>
          )}

          <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-semibold text-gray-800">Vehicle Images</h2>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                  <FaGem className="text-purple-500" /> Free Limit: {premiumConfig?.free_image_limit || 4}
              </div>
          </div>
          
          {premiumConfig?.camera_feature_enabled && (
              <div className="bg-blue-50 p-3 rounded mb-6 text-sm text-blue-800 flex items-start gap-2">
                <FaInfoCircle className="mt-1 flex-shrink-0" />
                <p><strong>Pro Tip:</strong> Use the <FaCamera className="inline" /> icon to snap photos. If you exceed the free limit, you can pay using your wallet balance.</p>
              </div>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.keys(imageUrls).map((viewType) => (
              <div key={viewType} className="relative group">
                <label className="block text-xs font-bold text-gray-600 mb-1 capitalize">
                  {viewType.replace('_', ' ')} {viewType === 'front' && <span className="text-red-500">*</span>}
                </label>
                <div className={`border-2 border-dashed rounded-lg h-32 flex flex-col items-center justify-center relative overflow-hidden transition-colors ${imageUrls[viewType] ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-primary hover:bg-blue-50'}`}>
                  {uploadingSlot === viewType ? (
                    <div className="flex flex-col items-center text-primary"><FaSpinner className="animate-spin text-xl mb-1" /><span className="text-[10px]">Uploading...</span></div>
                  ) : imageUrls[viewType] ? (
                    <>
                        <img src={imageUrls[viewType]} alt={viewType} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setImageUrls(prev => ({ ...prev, [viewType]: null }))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><FaTrash size={10} /></button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-2 w-full px-2">
                        {premiumConfig?.camera_feature_enabled && (
                            <button type="button" onClick={() => handleCameraClick(viewType)} className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-md transition-transform hover:scale-110" title="Snap & Save"><FaCamera /></button>
                        )}
                        <label className="cursor-pointer text-gray-500 hover:text-primary flex flex-col items-center">
                            <FaFolderOpen className="text-xl" />
                            <span className="text-[10px] mt-1">Browse</span>
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileInput(e, viewType)} />
                        </label>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Vehicle Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Make</label><input type="text" name="make" required value={formData.make} onChange={handleChange} placeholder="e.g. Toyota" className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Model</label><input type="text" name="model" required value={formData.model} onChange={handleChange} placeholder="e.g. Camry" className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><input type="number" name="year" required value={formData.year} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Price (NGN)</label><input type="number" name="price" required value={formData.price} onChange={handleChange} placeholder="e.g. 5000000" className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Condition</label><select name="condition" value={formData.condition} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white"><option value="nigerian_used">Nigerian Used</option><option value="foreign_used">Foreign Used (Tokunbo)</option><option value="new">Brand New</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Transmission</label><select name="transmission" value={formData.transmission} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white"><option value="automatic">Automatic</option><option value="manual">Manual</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Mileage (km)</label><input type="number" name="mileage" required value={formData.mileage} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label><input type="text" name="color" required value={formData.color} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
            <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">VIN / Chassis Number</label><input type="text" name="vin" value={formData.vin} onChange={handleChange} placeholder="17-character VIN (Optional but recommended)" className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none uppercase" maxLength={17} /></div>
          </div>

          {/* --- INSPECTION REQUEST --- */}
          <div className="bg-purple-50 p-4 rounded border border-purple-200 mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                      type="checkbox" 
                      name="request_inspection" 
                      checked={formData.request_inspection} 
                      onChange={handleChange} 
                      className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500" 
                  />
                  <div>
                      <span className="block font-bold text-purple-800 flex items-center gap-2">
                          <FaClipboardCheck /> Request Verified Inspection
                      </span>
                      <span className="text-xs text-purple-600">
                          Get a "Verified" badge. An expert will inspect your car. Fee: {helpers.formatCurrency(dynamicInspectionFee, 'NGN')}
                      </span>
                  </div>
              </label>
          </div>

          {/* --- POLICIES SECTION --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 bg-blue-50 p-4 rounded border border-blue-100">
              <div>
                  <label className="block text-sm font-bold text-blue-800 mb-1">Pay on Delivery</label>
                  <select name="pay_on_delivery_status" value={formData.pay_on_delivery_status} onChange={handleChange} className="w-full px-4 py-2 border rounded bg-white"><option value="Available">Available</option><option value="Not Available">Not Available</option></select>
              </div>
              <div>
                  <label className="block text-sm font-bold text-blue-800 mb-1">Refund Policy</label>
                  <select name="refundable_status" value={formData.refundable_status} onChange={handleChange} className="w-full px-4 py-2 border rounded bg-white"><option value="Not Specified">Not Specified</option><option value="Refundable">Refundable</option><option value="Non-Refundable">Non-Refundable</option></select>
              </div>
          </div>

          {/* --- DYNAMIC LOCATION DROPDOWNS --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select 
                    name="location_state" 
                    value={formData.location_state} 
                    onChange={handleChange} 
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white"
                >
                    <option value="">Select State</option>
                    {Object.keys(groupedLgas).sort().map(state => (
                        <option key={state} value={state}>{state}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LGA / City</label>
                <select 
                    name="location_lga" 
                    value={formData.location_lga} 
                    onChange={handleChange} 
                    disabled={!formData.location_state}
                    className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none bg-white disabled:bg-gray-100"
                >
                    <option value="">Select LGA</option>
                    {availableLgas.map(lga => (
                        <option key={lga} value={lga}>{lga}</option>
                    ))}
                </select>
            </div>
          </div>

          <div className="mb-6"><label className="block text-sm font-medium text-gray-700 mb-1">Features (Comma separated)</label><input type="text" name="features" value={formData.features} onChange={handleChange} placeholder="e.g. Leather Seats, Sunroof, Reverse Camera" className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" /></div>
          <div className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <button
                type="button"
                onClick={generateDescriptions}
                disabled={aiLoading || (!formData.make && !formData.model)}
                title={(!formData.make && !formData.model) ? 'Fill in the car details first' : 'Let AI write it for you'}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-full"
              >
                {aiLoading ? <FaSpinner className="animate-spin" /> : <FaMagic />}
                {aiLoading ? 'Writing...' : 'Generate with AI'}
              </button>
            </div>
            <textarea
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the car, or tap 'Generate with AI' above."
              className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
            ></textarea>

            {aiOpen && (
              <div className="mt-3 border border-purple-200 bg-purple-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-purple-800 flex items-center gap-2">
                    <FaMagic /> Pick a description
                  </p>
                  <button type="button" onClick={() => setAiOpen(false)} className="text-purple-400 hover:text-purple-700">
                    <FaTimes />
                  </button>
                </div>

                {aiLoading ? (
                  <div className="text-sm text-purple-700 flex items-center gap-2 py-4 justify-center">
                    <FaSpinner className="animate-spin" /> Writing 3 descriptions from your car details...
                  </div>
                ) : aiError ? (
                  <p className="text-sm text-red-600 py-2">{aiError}</p>
                ) : (
                  <div className="space-y-2">
                    {aiOptions.map((opt, i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => chooseDescription(opt)}
                        className="w-full text-left bg-white border border-purple-100 hover:border-purple-400 hover:shadow-sm rounded p-3 text-sm text-gray-700 transition"
                      >
                        <span className="block font-semibold text-purple-700 mb-1">Option {i + 1} — tap to use</span>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {!aiLoading && (
                  <button
                    type="button"
                    onClick={generateDescriptions}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 border border-purple-300 hover:bg-purple-100 px-3 py-1.5 rounded-full"
                  >
                    <FaSyncAlt /> Regenerate fresh options
                  </button>
                )}
              </div>
            )}
          </div>

          {/* --- COST SUMMARY (RESPONSIVE FIX) --- */}
          {totalCost > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6 animate-fadeIn">
                  <h4 className="font-bold text-yellow-800 mb-2 flex items-center gap-2"><FaMoneyBillWave /> Payment Required</h4>
                  <ul className="text-sm text-yellow-700 space-y-1 mb-3">
                      {breakdown.map((item, idx) => (
                          <li key={idx} className="flex justify-between items-start gap-4">
                              <span className="text-left">{item.label} {item.note && <span className="text-xs opacity-75 block sm:inline">({item.note})</span>}</span>
                              <span className="font-bold whitespace-nowrap">{helpers.formatCurrency(item.amount, 'NGN')}</span>
                          </li>
                      ))}
                  </ul>
                  
                  {/* --- HUGE RED PRICE TAG (RESPONSIVE) --- */}
                  <div className="border-t border-yellow-200 pt-3 mt-2 flex flex-col sm:flex-row justify-between items-center gap-2 font-black text-xl text-yellow-900">
                      <span className="text-lg sm:text-xl">Total To Pay:</span>
                      <span className="text-2xl sm:text-3xl text-red-600 break-all">{helpers.formatCurrency(totalCost, 'NGN')}</span>
                  </div>
                  
                  <p className="text-xs text-yellow-600 mt-2 text-center sm:text-left">
                      This amount will be deducted from your Spending Wallet (Balance: {helpers.formatCurrency(realTimeBalance, 'NGN')}).
                  </p>

                  {/* Not enough in the wallet? A short hint; full options pop up on submit. */}
                  {realTimeBalance < totalCost && (
                      <button
                          type="button"
                          onClick={() => { setFundShortfall({ total: totalCost, balance: realTimeBalance }); setShowFundModal(true); }}
                          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm font-bold px-4 py-2.5 rounded hover:bg-red-100"
                      >
                          <FaWallet /> Balance too low — tap to see how to pay
                      </button>
                  )}
              </div>
          )}

          <button 
            type="submit" 
            disabled={loading || uploadingSlot !== null}
            className={`w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-6 rounded-lg transition duration-300 shadow-md text-sm sm:text-base ${loading || uploadingSlot !== null ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Submitting...' : (totalCost > 0 ? `Pay ${helpers.formatCurrency(totalCost, 'NGN')} & Submit` : 'Submit Listing')}
          </button>

        </form>
      </div>

      <CameraModal 
          isOpen={isCameraOpen} 
          onClose={() => setIsCameraOpen(false)} 
          onCapture={handleCameraCapture}
          viewType={activeViewType || 'image'}
      />

      <CreditPurchaseModal 
          isOpen={isCreditModalOpen}
          onClose={() => setIsCreditModalOpen(false)}
          type="listing"
      />

      {/* --- INSUFFICIENT FUNDS POP-UP --- */}
      {showFundModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-start sm:items-center justify-center bg-black/70 p-3 overflow-y-auto"
          onClick={() => setShowFundModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md my-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FaWallet className="text-red-500 text-xl flex-shrink-0" />
                <h3 className="text-lg font-bold text-gray-800">Not enough wallet balance</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowFundModal(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Amount to pay:</span>
                  <span className="font-bold text-gray-900 whitespace-nowrap">{helpers.formatCurrency(fundShortfall.total, 'NGN')}</span>
                </div>
                <div className="flex justify-between gap-3 mt-1">
                  <span className="text-gray-600">Your wallet balance:</span>
                  <span className="font-bold text-gray-900 whitespace-nowrap">{helpers.formatCurrency(fundShortfall.balance, 'NGN')}</span>
                </div>
                <div className="flex justify-between gap-3 mt-1 pt-2 border-t border-red-200">
                  <span className="font-semibold text-red-700">You need to add:</span>
                  <span className="font-bold text-red-700 whitespace-nowrap">
                    {helpers.formatCurrency(Math.max(0, fundShortfall.total - fundShortfall.balance), 'NGN')}
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3">
                Fund your wallet using any option below. Once we confirm your payment,
                your balance updates and you can submit your listing.
              </p>

              <BankTransferInfo />

              <button
                type="button"
                onClick={() => setShowFundModal(false)}
                className="w-full mt-4 border border-gray-300 text-gray-700 font-bold py-2.5 rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SellCarPage;
